import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, skip } from 'rxjs/operators';
import { UserApi } from '@shared-features/services/user-api.service';
import { BidChatTarget, BidChatView } from '@features/bid-chat/models/bid-chat.model';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { FeatureFlagConstant } from '@shared/constants/feature-flag.constant';

/** A chat message that came in. `conversationId` (Firestore doc id) is unknown on a push without it. */
export interface BidChatMessageEvent {
  shipmentId: number | null;
  conversationId?: string | null;
}

/**
 * What the chat drawer shows (#47189). The drawer is the layout's single `app-drawer`, which has
 * no per-page binding site, so the header and the bids list open it through this service and
 * `MainPageComponent` follows `view$`. `null` = closed.
 */
@Injectable({ providedIn: 'root' })
export class BidChatDrawerService {
  private readonly viewSubject$ = new BehaviorSubject<BidChatView | null>(null);
  readonly view$ = this.viewSubject$.asObservable();

  private readonly threadLeftSubject$ = new Subject<BidChatTarget>();
  /**
   * A thread the customer just left (drawer closed, back to Chat logs, or another thread opened).
   * Prices or the bid itself may have changed in it, so its shipment's bids list reloads.
   */
  readonly threadLeft$ = this.threadLeftSubject$.asObservable();

  private readonly chatMessageSubject$ = new Subject<BidChatMessageEvent>();
  /**
   * A chat message that just came in (live listener or push). A new offer may have changed a bid's
   * price while the chat was closed, so that shipment's bids list reloads.
   */
  readonly chatMessage$ = this.chatMessageSubject$.asObservable();

  /** Off unless the LaunchDarkly flag is exactly `true`, so a missing flag or LD outage keeps chat off. */
  readonly chatEnabled$: Observable<boolean>;

  /** The Chat logs tab last chosen, so Back from a thread returns to it. */
  logsTab = 0;

  /** The conversation the open thread started with its first message; its `target` doesn't carry it. */
  private startedConversationId: string | null = null;

  constructor(userApi: UserApi, private featureFlagService: FeatureFlagService) {
    this.chatEnabled$ = this.featureFlagService.isEnabled$(FeatureFlagConstant.CustomerWebChat).pipe(
      map((enabled) => enabled === true),
      distinctUntilChanged()
    );
    // Turned off while open: the drawer must not keep showing a disabled feature.
    this.chatEnabled$.pipe(filter((enabled) => !enabled)).subscribe(() => this.close());

    // Logout or another account: nothing of the last user's chat may reopen on the next login.
    userApi.currentUser$
      .pipe(
        map((user) => user?.id ?? null),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe(() => {
        this.logsTab = 0;
        this.viewSubject$.next(null);
      });
  }

  openLogs(): void {
    this.show({ mode: 'logs' });
  }

  /** `fromLogs` adds the back arrow; a thread opened from a bid has none, per design. */
  openThread(target: BidChatTarget, fromLogs = false): void {
    this.show({ mode: 'thread', target, fromLogs });
  }

  /** A thread opened from a bid just created its conversation (the first message). */
  conversationStarted(target: BidChatTarget, firestoreConversationId: string): void {
    const view = this.viewSubject$.value;
    if (view?.mode === 'thread' && view.target === target) {
      this.startedConversationId = firestoreConversationId;
    }
  }

  /**
   * Whether the drawer shows this chat now (a push for it then needs no toast). With a conversation id
   * only that id matches: another bidder on the same shipment has a conversation of their own.
   */
  isShowingChat({ conversationId, shipmentId }: { conversationId?: string | null; shipmentId?: number | null }): boolean {
    const view = this.viewSubject$.value;
    if (view?.mode !== 'thread') return false;
    if (conversationId) {
      return conversationId === (view.target.conversation?.firestoreConversationId || this.startedConversationId);
    }
    return !!shipmentId && String(view.target.shipmentId) === String(shipmentId);
  }

  chatMessageReceived(event: BidChatMessageEvent): void {
    if (event.shipmentId) {
      this.chatMessageSubject$.next(event);
    }
  }

  close(): void {
    if (this.viewSubject$.value) {
      this.show(null);
    }
  }

  private show(view: BidChatView | null): void {
    // Every entry point (buttons, notifications, push links) opens through here; closing is always allowed.
    if (view && this.featureFlagService.isEnabled(FeatureFlagConstant.CustomerWebChat) !== true) return;
    const previous = this.viewSubject$.value;
    this.startedConversationId = null;
    this.viewSubject$.next(view);
    if (previous?.mode === 'thread') {
      this.threadLeftSubject$.next(previous.target);
    }
  }
}
